import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from '../../shared/utils.service';
import { ApiDataSource } from '../../utils/api-data-source';
import { AccessRequestsDeclineDialogComponent } from './access-requests-decline-dialog/access-requests-decline-dialog.component';
import { AccessRequestsFilterComponent } from './access-requests-filter/access-requests-filter.component';
import { AccessRequestsService } from './access-requests.service';

/**
 * The account's membership approval queue (SC-301).
 *
 * A flat list with a status column and a status filter rather than three sub-lists. One ApiDataSource gives
 * search, sort, pagination and bulk selection for free; splitting pending, declined and approved into tabs
 * would triplicate all of it for no gain.
 *
 * Declined rows stay visible and stay approvable. That is deliberate and load bearing: a user who has hit
 * the re-request cap cannot ask again, so approving the existing declined row is the only way back in. If
 * Approve were pending-only, a reviewer who declined somebody three times by mistake would have locked them
 * out permanently with no way to undo it.
 */
@Component({
  selector: 'app-access-requests',
  templateUrl: './access-requests.component.html',
  styleUrls: ['./access-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccessRequestsComponent implements OnInit, AfterViewInit {
  // Resolved ONCE in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;

  // Built dynamically, never a static array.
  displayedColumns: string[] = [];

  listDataSource = new AccessRequestsDataSource(this.app, this.api);

  // How the person reached the account. Shown because it is the main signal a reviewer has: a request
  // raised at a site kiosk carries different weight from one typed into a registration form.
  // Keys MUST match UserAccountLink::ORIGIN_* on the API exactly. They were wrong on the first pass
  // ('registration' for 'register', 'qr_sign_in' for 'qr_signin', and so on), so every row fell through
  // to the raw database value and the column read "register" instead of "Registration".
  private readonly originLabels: { [key: string]: string } = {
    register: 'Registration',
    employer_selection: 'Employer Selection',
    qr_signin: 'QR Code Sign In',
    kiosk_signin: 'Kiosk Sign In',
    employee_added: 'Added As Employee',
    contractor_added: 'Added As Contractor',
    super_admin: 'Super Admin',
    import: 'Import',
    system: 'System'
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    private api: ApiRequestService,
    public rbac: RBACService,
    private accessRequestsService: AccessRequestsService
  ) {}

  ngOnInit(): void {
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES_ACCESS_REQUESTS, RBACService.RBAC_A.A_VIEW)
    ], true);

    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES_ACCESS_REQUESTS, RBACService.RBAC_A.A_EDIT)
    ], true);

    // No page-level redirect here. The parent shell already redirects when neither tab is permitted, and
    // this component is only rendered inside its own permitted tab.

    // The select column exists solely for bulk approve and decline, so it is gated on canEdit rather than
    // the usual canView || canDelete. There is no export and no bulk delete on this surface, so a
    // view-only reviewer has nothing to select for.
    this.displayedColumns = [];
    if ( this.canEdit ) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'contact_person',
      'claimed_company',
      // Sits next to claimed_company because the two are the same kind of thing: context the requester
      // supplied about themselves, neither of it verified. Both are read together when deciding.
      'request_reason',
      'status',
      'request_origin',
      'requested_at'
    );
    if ( this.canEdit ) {
      this.displayedColumns.push('actions');
    }

    if ( this.canView ) {
      this.listDataSource.getData(true);
    }
  }

  ngAfterViewInit(): void {
    // The table and its paginator live inside a container gated on canView, so without the permission
    // Angular never creates them and these view children stay undefined.
    if ( !this.canView ) {
      return;
    }

    this.sort.sortChange.subscribe((): number => (this.paginator.pageIndex = 0));

    const _tap = tap((): void => {
      this.listDataSource.limit = this.paginator.pageSize;
      this.listDataSource.offset = this.paginator.pageIndex;
      this.listDataSource.order = this.sort.direction;
      this.listDataSource.order_by = this.sort.active;
      this.listDataSource.getData();
    });

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Opens the filter dialog and applies the result.
   *
   * The offset is reset and the selection cleared on apply. Without the reset, moving from a long pending
   * list to a short declined one can land the user on a page that no longer exists and show them an empty
   * table. Without clearing the selection, a bulk action could be applied to rows the user can no longer
   * see, which is the worse of the two.
   */
  onOpenFilters(): void {
    this.utils.showComponentDialog(
      AccessRequestsFilterComponent,
      {
        status: this.listDataSource.status
      },
      { width: '400px' }
    ).then((response): void => {
      if ( !response ) {
        return;
      }
      this.listDataSource.status = response.status;
      this.listDataSource.selection.clear();
      this.listDataSource.getData(true);
    });
  }

  /**
   * Human label for a request origin, falling back to the raw value so an origin added on the API side
   * still shows something meaningful rather than a blank cell.
   */
  getOriginLabel(origin: string): string {
    return this.originLabels[origin] || origin || 'Unknown';
  }

  /**
   * Explains what a status means on hover.
   *
   * The declined wording states that the row can still be approved, because that is not obvious and it is
   * the way out for somebody who has run out of attempts to ask again.
   */
  getStatusTooltip(status: string): string {
    switch ( status ) {
      case 'pending':
        return 'This person is waiting for someone to approve their request.';
      case 'approved':
        return 'This person is an approved member of the account.';
      case 'declined':
        return 'This request was not approved. It can still be approved from here.';
      default:
        return '';
    }
  }

  onApprove(request: any): void {
    if ( !this.canEdit ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to approve access requests.');
      return;
    }

    // The copy states the consequence rather than just asking to confirm. Approving is not merely clearing
    // a queue item: it assigns the account's default role, moves the person's employer onto this account,
    // and assigns the account's inductions and forms to them.
    this.utils.showModal(
      'Approve Access Request',
      '<p>Approve <b>' + (request.contact_person || 'this person') + '</b> to join ' +
      (this.app.account?.name || 'this account') + '?</p>' +
      '<p>They will be given the default user role, this account becomes their employer, and the ' +
      'account\'s inductions and forms will be assigned to them.</p>',
      (): void => {
        this.api.makeRequest('put', `v2/account-access-requests/${request.id}/approve`)
          .then((): void => {
            this.utils.showToast('The request was approved.');
            this.afterAction();
          })
          .catch((error): void => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

  /**
   * Every partner link id of the account (unfiltered by search, status tab and pagination).
   */
  get partnerManagedLinkIds(): number[] {
    return this.listDataSource.lastResponse?.partnerManagedLinkIds ?? [];
  }

  onDecline(request: any): void {
    if ( !this.canEdit ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to decline access requests.');
      return;
    }
    // Partner memberships are managed by support.
    if ( this.rbac.partnerLockApplies(!!request?.is_partner_managed) ) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    this.utils.showComponentDialog(
      AccessRequestsDeclineDialogComponent,
      { name: request.contact_person },
      { width: '600px' }
    ).then((result): void => {
      if ( !result ) {
        return;
      }
      this.api.makeRequest('put', `v2/account-access-requests/${request.id}/decline`, {
        reason: result.reason
      })
        .then((): void => {
          this.utils.showToast('The request was declined.');
          this.afterAction();
        })
        .catch((error): void => {
          this.utils.handleAPIErrors(error);
        });
    });
  }

  onBulkApprove(): void {
    if ( !this.canEdit ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to approve access requests.');
      return;
    }
    if ( !this.listDataSource.selection.hasValue() ) {
      this.utils.showToast('You need to select some requests first.');
      return;
    }

    const count: number = this.listDataSource.selection.selected.length;

    this.utils.showModal(
      'Approve Selected Requests',
      '<p>Approve the selected ' + count + ' request(s) to join ' +
      (this.app.account?.name || 'this account') + '?</p>' +
      '<p>Each person will be given the default user role, this account becomes their employer, and the ' +
      'account\'s inductions and forms will be assigned to them.</p>',
      (): void => {
        this.submitBulk('approve');
      }
    );
  }

  onBulkDecline(): void {
    if ( !this.canEdit ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to decline access requests.');
      return;
    }
    if ( !this.listDataSource.selection.hasValue() ) {
      this.utils.showToast('You need to select some requests first.');
      return;
    }
    // Partner memberships are managed by support (the id list covers pages that were never loaded).
    const lockedLinkIds: number[] = this.partnerManagedLinkIds;
    if (
      this.rbac.partnerLockApplies(true)
      && this.listDataSource.selection.selected.some((id: number): boolean => lockedLinkIds.includes(id))
    ) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    this.utils.showComponentDialog(
      AccessRequestsDeclineDialogComponent,
      { count: this.listDataSource.selection.selected.length },
      { width: '600px' }
    ).then((result): void => {
      if ( !result ) {
        return;
      }
      this.submitBulk('decline', result.reason);
    });
  }

  /**
   * Posts a bulk action and reports the PER-ID outcome.
   *
   * A blanket success toast would be wrong here. Individual rows can legitimately fail while the rest
   * succeed, for instance a request that was already approved by another administrator moments earlier, so
   * claiming the whole batch worked would leave the reviewer believing people have access who do not.
   */
  private submitBulk(action: string, reason: string = ''): void {
    this.api.makeRequest('put', 'v2/account-access-requests/bulk', {
      ids: this.listDataSource.selection.selected,
      action: action,
      reason: reason
    })
      .then((response: any): void => {
        const succeeded: number = response?.data?.succeeded || 0;
        const failed: number = response?.data?.failed || 0;
        const verb: string = action === 'approve' ? 'approved' : 'declined';

        if ( failed === 0 ) {
          this.utils.showToast(succeeded + ' request(s) were ' + verb + '.');
        } else {
          // Name the failures so the reviewer can act on them, rather than reporting a bare count they
          // cannot do anything with.
          const reasons: string[] = (response?.data?.results || [])
            .filter((r: any): boolean => !r.success)
            .map((r: any): string => '<li>Request ' + r.id + ': ' + (r.message || 'could not be updated') + '</li>');

          this.utils.showModal(
            'Some Requests Were Not Updated',
            '<p>' + succeeded + ' request(s) were ' + verb + '. ' + failed + ' could not be updated:</p>' +
            '<ul>' + reasons.join('') + '</ul>'
          );
        }

        this.listDataSource.selection.clear();
        this.afterAction();
      })
      .catch((error): void => {
        this.utils.handleAPIErrors(error);
      });
  }

  /**
   * Refreshes the list and the badge after any action, so the count never lags what the reviewer just did.
   */
  private afterAction(): void {
    this.listDataSource.getData();
    this.accessRequestsService.refreshPendingCount();
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class AccessRequestsDataSource extends ApiDataSource {
  // Pending first, because that is the actionable queue. Somebody opening this tab wants the work, not
  // the archive.
  status: string = 'pending';

  order_by = 'requested_at';
  order = 'desc';

  getData(resetOffset: boolean = false) {
    return this.makeRequest('v2/account-access-requests', resetOffset, {
      status: this.status
    });
  }
}
